<template>
  <v-col :cols="cols">
    <v-card class="mx-1 mb-1">
      <v-card-title class="pa-6 pb-0">
        <v-row  no-gutters>
          <v-col cols="11">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
              <div class="chartTitle textColor2--text"
                v-bind="attrs"
                v-on="on">
                {{ $t(chartName) }}
              </div>
              </template>
              <span>{{ $t(chartName) }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="1">
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text class="pa-6">
        <v-row>
          <v-col>
            <ApexChart
              v-if="apexLoading"
              height="350"
              type="line"
              :options="chartOptions"
              :series="dataSeries"
            ></ApexChart>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import ApexChart from "vue-apexcharts";
import { Component, Prop, Watch } from "vue-property-decorator";
import { TimeUtil } from '@/helpers/TimeUtil'
import i18n from "@/i18n";
import { devicesModule } from '@/store/modules/store-accessor'
import dayjs from "dayjs";

@Component({
  components: { ApexChart },
})
export default class DeviceSideviewLineChart extends Vue {
  @Prop() readonly cols: number;
  @Prop() readonly chartName: string;
  @Prop() readonly deviceId: string;
  @Prop() readonly dateRange: Record<string, any>;
  @Prop() dataMetrics: Array<any>;
  @Prop({default: 'historical'}) typeOfData: string;
  
  apexLoading = false;

  chartOptions = {
    chart: {
      height: 380,
      width: "100%",
      type: "area",
      animations: {
        initialAnimation: {
          enabled: false
        }
      },
      zoom: {
        allowMouseWheelZoom: false,
      },
      events: {
        beforeZoom: (chartContext: any, { xaxis }: any) => {
          let time_from = parseInt(xaxis.min) * 1000000
          let time_to = parseInt(xaxis.max) * 1000000
          let updatedDateRange = { date_from: time_from, date_to: time_to };
          this.$emit('update-date-range', updatedDateRange);

          if(this.typeOfData === 'metadata') {
            devicesModule.getDeviceMetadata({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
          }
          else {
            devicesModule.getDeviceData({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
          }
        }
      },
      toolbar: {
        tools: {
          pan: false,
          reset: false
        },
        export: {
          csv: {
            filename: "File Name",
            columnDelimiter: ',',
            headerCategory: 'timestamp',
            headerValue: 'value',
            categoryFormatter(x: any) {
              return x
            },
            valueFormatter(y: any) {
              return y
            }
          },
          svg: {
            filename: '',
          },
          png: {
            filename: '',
          }
        }
      },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    legend: {
      formatter: function(seriesName: any, opts: any) :any {

        return [i18n.t(seriesName)]
      }
    },
    yaxis: {
      decimalsInFloat: 1
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false
      }
    },
    tooltip: {
      y: {
        formatter: (val: any) => {
         if(val) {
           return val.toFixed(1)
         }
        },
        title: {
          formatter: (seriesName: string) => this.$i18n.t(seriesName),
        },
      },
      x: {
        formatter: (val: any) => {
          if(val) {
           return TimeUtil.formatInfluxDateTime(val) 
          }
        }
      }
    }
  };

  async mounted() {
    //Fetch initial values for date range 1

    const time_to = dayjs().valueOf() * 1000000//ValueOf gives time in Milliseconds time in browser local time
    const time_from = dayjs().subtract(this.chartApexAreaSelect.days, 'days').valueOf() * 1000000//ValueOf gives time in Milliseconds time in browser local time
    if(this.typeOfData === 'metadata') {
      devicesModule.getDeviceMetadata({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
    }
    else {
      devicesModule.getDeviceData({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
    }
    
    setTimeout(() => {
      this.apexLoading = true;
    });

    let exportFileName = `${this.deviceId}-${(this.$i18n.t(this.chartName)).toString().replace(/ /g,"_")}_export` ;
    this.chartOptions.chart.toolbar.export.csv.filename = exportFileName;
    this.chartOptions.chart.toolbar.export.svg.filename = exportFileName;
    this.chartOptions.chart.toolbar.export.csv.filename = exportFileName;
  }

  selectTimeRange = [
    { days: 1, text: this.$i18n.t("day") },
    { days: 7, text: this.$i18n.t("week") },
    { days: 30, text: this.$i18n.t("month") }
  ]

  chartApexAreaSelect = this.selectTimeRange[0];


  @Watch('dateRange')
  async changeDataTimeRange() {
    const time_to = this.dateRange.date_to //ValueOf gives time in Milliseconds time in browser local time
    const time_from = this.dateRange.date_from//ValueOf gives time in Milliseconds time in browser local time

    // const time_to = this.dateRange.time_to.valueOf() * 1000000//ValueOf gives time in Milliseconds time in browser local time
    // const time_from = this.dateRange.time_from.valueOf() * 1000000//ValueOf gives time in Milliseconds time in browser local time
    if(this.typeOfData === 'metadata') {
      devicesModule.getDeviceMetadata({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
    }
    else {
      devicesModule.getDeviceData({ deviceId: this.deviceId, time_from: time_from, time_to: time_to, metric: this.dataMetrics })
    }
  }

  get dataSeries() {
    let series: Array<any> = [];
    this.dataMetrics.forEach(element => {
      series.push(devicesModule.currDeviceData[element])
    });

    return series.filter(Boolean)
  }

}
</script>

<style lang="scss">
@import "../../styles/app";

.dashboard-page {
  .page-title {
    padding-bottom: 20px;
    @include media-breakpoint-up(sm) {
      padding-bottom: 0;
    }
  }
  .apexcharts-legend-series {
    padding: 2px 5px 3px;
    display: flex;
    align-items: center;
    .apexcharts-legend-marker {
      margin-right: 8px;
    }
  }
  .main-chart-select {
    font-size: 1rem;
    margin-right: 10px;
    fieldset {
      border-width: 1px !important;
      border-color: $text-grey !important;
      color: $text-grey !important;
    }
    .v-select__selection {
      color: $text-color !important;
    }
  }
  .v-card {
    .v-card__text {
      .subtext {
        font-size: 1.142rem;
        font-weight: 400;
        color: $title-grey;
      }
      .subtext-index {
        font-size: 11px;
        color: $text-color;
        font-weight: 400;
      }
    }
  }
}

.select {
    width: 140px;
    max-width: 200px;
}

.v-menu__content {
  box-shadow: $card-shadow !important;
  .v-list-item__content .v-list-item__title {
    font-size: 1rem;
    font-weight: 400;
  }
}
.button-shadow {
  box-shadow: $button-shadow;
}

p {
  font-size: 14px;
  min-width: 150px;
  max-width: 190px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
